import { useQuery } from '@tanstack/react-query'
import { Search, SlidersHorizontal, X } from 'lucide-react'
import { useId, useState } from 'react'

import { loanOfficersQueryOptions } from '@/api/reference-api'
import type { GroupStatus } from '@/api/types'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select'
import { useIsAtLeast } from '@/lib/use-viewport'
import { cn } from '@/lib/utils'

import { GROUP_STATUSES, groupStatusLabel } from './constants'

/**
 * The group list toolbar.
 *
 * `ANY` stands in for "no filter", because a select option cannot carry an empty value. On a
 * narrow screen the search box stays and the narrowing controls fold behind a disclosure — the
 * same shape the customer list uses, and for the same reason: somebody reaching for this from a
 * phone has a group name to type, and stacked controls would push the results below the fold
 * before the search has run.
 *
 * The officer filter is offered only when the directory can be read. A select with nothing in it
 * says "no officers exist", which is a different and wrong statement from "your role cannot see
 * the staff list".
 */
const ANY = 'any'

export interface GroupFiltersProps {
  searchText: string
  onSearchTextChange: (value: string) => void
  status: GroupStatus | undefined
  onStatusChange: (status: GroupStatus | undefined) => void
  loanOfficerId: string | undefined
  onLoanOfficerChange: (loanOfficerId: string | undefined) => void
  /** The branch the officer directory is read for — the one the operator is working in. */
  branchId: string | null
  hasActiveFilters: boolean
  onClearFilters: () => void
}

export function GroupFilters({
  searchText,
  onSearchTextChange,
  status,
  onStatusChange,
  loanOfficerId,
  onLoanOfficerChange,
  branchId,
  hasActiveFilters,
  onClearFilters,
}: GroupFiltersProps) {
  const searchId = useId()
  const statusId = useId()
  const officerId = useId()
  const narrowingId = useId()

  const compact = !useIsAtLeast('sm')
  const officers = useQuery(loanOfficersQueryOptions(branchId))
  const narrowingCount = [status, loanOfficerId].filter(Boolean).length
  const [narrowingOpen, setNarrowingOpen] = useState(narrowingCount > 0)

  return (
    <div className="space-y-1.5 rounded-card border border-border-subtle bg-surface p-3">
      <div className="grid gap-3 sm:flex sm:flex-wrap sm:items-end">
        <div className="min-w-0 space-y-1.5 sm:min-w-64 sm:flex-1">
          <Label htmlFor={searchId}>Search</Label>
          <div className="relative">
            <Search
              className="pointer-events-none absolute top-1/2 left-2.5 size-4 -translate-y-1/2 text-muted"
              aria-hidden
            />
            <Input
              id={searchId}
              value={searchText}
              // Not on a phone: the same focus raises the on-screen keyboard over the results the
              // operator came to read, before they have asked for anything.
              autoFocus={!compact}
              autoComplete="off"
              spellCheck={false}
              type="search"
              placeholder="Group name or group code"
              className="pr-8 pl-8"
              onChange={(event) => onSearchTextChange(event.target.value)}
              onKeyDown={(event) => {
                if (event.key === 'Escape' && searchText !== '') {
                  event.preventDefault()
                  onSearchTextChange('')
                }
              }}
            />
            {searchText ? (
              <button
                type="button"
                onClick={() => onSearchTextChange('')}
                className="absolute top-1/2 right-1.5 flex size-6 -translate-y-1/2 items-center justify-center rounded-control text-muted transition-colors hover:bg-surface-hover hover:text-heading max-sm:size-11"
                aria-label="Clear the search"
                tabIndex={-1}
              >
                <X className="size-3.5" />
              </button>
            ) : null}
          </div>
        </div>

        {compact ? (
          <div className="flex items-center gap-2">
            <Button
              variant="secondary"
              className="flex-1"
              aria-expanded={narrowingOpen}
              aria-controls={narrowingId}
              onClick={() => setNarrowingOpen((open) => !open)}
            >
              <SlidersHorizontal />
              Filters
              {narrowingCount > 0 ? <Badge tone="brand">{narrowingCount}</Badge> : null}
            </Button>
            {hasActiveFilters ? (
              <Button variant="ghost" onClick={onClearFilters}>
                <X />
                Clear
              </Button>
            ) : null}
          </div>
        ) : null}

        <div
          id={narrowingId}
          className={cn('grid grid-cols-1 gap-3 sm:contents', compact && !narrowingOpen && 'hidden')}
        >
          <div className="space-y-1.5 sm:w-44">
            <Label htmlFor={statusId}>Status</Label>
            <Select
              value={status ?? ANY}
              onValueChange={(value) =>
                onStatusChange(value === ANY ? undefined : (value as GroupStatus))
              }
            >
              <SelectTrigger id={statusId}>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value={ANY}>Any status</SelectItem>
                {GROUP_STATUSES.map((value) => (
                  <SelectItem key={value} value={value}>
                    {groupStatusLabel(value)}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {officers.data && officers.data.length > 0 ? (
            <div className="space-y-1.5 sm:w-56">
              <Label htmlFor={officerId}>Loan officer</Label>
              <Select
                value={loanOfficerId ?? ANY}
                onValueChange={(value) => onLoanOfficerChange(value === ANY ? undefined : value)}
              >
                <SelectTrigger id={officerId}>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value={ANY}>Any officer</SelectItem>
                  {officers.data.map((staff) => (
                    <SelectItem key={staff.userId} value={staff.userId}>
                      {staff.fullName}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          ) : null}
        </div>

        {!compact && hasActiveFilters ? (
          <Button variant="ghost" onClick={onClearFilters}>
            <X />
            Clear
          </Button>
        ) : null}
      </div>

      <p className="text-2xs text-muted">
        Group names and codes match on any part. Health is not a filter: a group that cannot be
        scored is not a low score, and a threshold typed into a filter box would be the wrong one —
        the qualifying threshold is branch-configurable and lives on the server.
      </p>
    </div>
  )
}
